// src/components/adminPage/timetable/exportPDF.ts
import jsPDF from 'jspdf'
import { toast } from '@/components/ui/use-toast'
import { API_URL } from '@/config/constants'
import { arabicFontBase64, arabicFontName } from '@/lib/fonts'

// Helper function to check if text contains Arabic characters
const containsArabic = (text: string): boolean => {
  const arabicRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/
  return arabicRegex.test(text)
}

// Helper function to clean teacher names appropriately
const cleanTeacherName = (teacherName: string): string => {
  if (!teacherName) return ''
  
  // If the text contains Arabic characters, don't clean it
  if (containsArabic(teacherName)) {
    return teacherName.trim()
  }
  
  // For non-Arabic text, remove special characters but keep spaces and letters
  return teacherName.replace(/[^a-zA-Z\s]/g, '').trim()
}

// Helper function to render text with Arabic support (like Quiz.ts)
const renderTextWithArabicSupport = (
  pdf: jsPDF,
  text: string,
  x: number,
  y: number,
  options: {
    align?: 'left' | 'center' | 'right'
    fontSize?: number
    fontStyle?: 'normal' | 'bold'
    maxWidth?: number
    baseline?: 'top' | 'middle' | 'bottom'
  } = {}
) => {
  const { align = 'center', fontSize = 10, fontStyle = 'normal', maxWidth, baseline = 'middle' } = options
  
  // Check if text contains Arabic characters
  const isArabic = containsArabic(text)
  
  // Set font size
  pdf.setFontSize(fontSize)
  
  if (isArabic) {
    // For Arabic text, use Arabic font
    pdf.setFont(arabicFontName)
    
    let textToRender = text
    if (maxWidth) {
      textToRender = pdf.splitTextToSize(text, maxWidth)
    }
    
    // Use right alignment for Arabic text (like Quiz.ts does)
    const arabicAlign = align === 'left' ? 'right' : align
    pdf.text(textToRender, x, y, {
      align: arabicAlign,
      baseline
    })
  } else {
    // For non-Arabic text, use regular font
    pdf.setFont('helvetica', fontStyle)
    
    let textToRender = text
    if (maxWidth) {
      textToRender = pdf.splitTextToSize(text, maxWidth)
    }
    
    pdf.text(textToRender, x, y, {
      align,
      baseline
    })
  }
}

// Helper function to truncate text if it's too long
const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength - 3) + '...'
}

// Format time for display (e.g., "08:00" => "8" or "08:30" => "8:30")
const formatTimeForDisplay = (timeStr: string): string => {
  if (!timeStr) return ''

  if (timeStr.includes(':')) {
    const [hours, minutes] = timeStr.split(':').map(Number)
    if (minutes === 0) {
      return hours.toString()
    }
    return timeStr
  }
  return timeStr
}

// Helper function to format period numbers to time format
const formatTimeSlot = (period: number): string => {
  // Convert period numbers to time format (e.g., 1 => "8:00 => 9:00")
  const startHour = 7 + period
  const endHour = startHour + 1
  return `${startHour} => ${endHour}`
}

// Helper function to convert time string to minutes for sorting
const convertTimeToMinutes = (timeStr: string): number => {
  // Handle different time formats
  if (timeStr.includes(':')) {
    // Format: "08:00"
    const [hours, minutes] = timeStr.split(':').map(Number)
    return hours * 60 + (minutes || 0)
  } else {
    // Format: "8" (just the hour)
    return parseInt(timeStr) * 60
  }
}

const getClassData = (className: string, timetableData: any) => {
  if (timetableData.scheduleData) {
    return timetableData.scheduleData.find(
      (item: any) => item.class === className
    )
  }

  if (timetableData.schedule && timetableData.schedule[className]) {
    const dayMap: Record<string, any[]> = {}

    timetableData.schedule[className].forEach((item: any) => {
      if (!dayMap[item.day]) {
        dayMap[item.day] = []
      }

      const timeValue =
        item.time ||
        (item.start && item.end
          ? `${item.start} => ${item.end}`
          : item.period !== undefined
            ? formatTimeSlot(item.period)
            : 'No time specified')

      dayMap[item.day].push({
        time: timeValue,
        subject: item.subject,
        teacher: item.teacher,
        salle: item.room || item.salle,
      })
    })

    return {
      class: className,
      ...dayMap,
    }
  }

  return null
}

// Updated function to always return all weekdays
const getAllWeekdays = (): string[] => {
  return [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ]
}

const getTimeSlots = (
  classData: any,
  days: string[],
  timeSlotData: any[] = []
): string[] => {
  // If we have timeSlotData from the backend, use it
  if (timeSlotData && timeSlotData.length > 0) {
    return [...timeSlotData]
      .sort((a, b) => a.period - b.period)
      .map(
        (slot) =>
          `${formatTimeForDisplay(slot.start)} => ${formatTimeForDisplay(slot.end)}`
      )
  }

  // Fallback to extracting from class data
  const timeSlotSet = new Set<string>()

  days.forEach((day) => {
    if (Array.isArray(classData[day])) {
      classData[day].forEach((lesson: any) => {
        if (lesson?.time) timeSlotSet.add(lesson.time)
      })
    }
  })

  let timeSlots = Array.from(timeSlotSet)
  if (timeSlots.length === 0) {
    // Default time slots if none found
    return [
      '8 => 9',
      '9 => 10',
      '10 => 11',
      '11 => 12',
      '13 => 14',
      '14 => 15',
      '15 => 16',
      '16 => 17',
    ]
  }

  return timeSlots.sort((a, b) => {
    const [aStartStr] = a.split('=>').map((s) => s.trim())
    const [bStartStr] = b.split('=>').map((s) => s.trim())
    return convertTimeToMinutes(aStartStr) - convertTimeToMinutes(bStartStr)
  })
}

// Function to fetch establishment data
// Function to fetch establishment data
const fetchEstablishmentData = async (): Promise<{name: string, logo: string} | null> => {
  try {
    // Step 1: Get user data to find etablissementId
    const userResponse = await fetch(`${API_URL}/user-controller/session`, {
      credentials: 'include'
    })
    if (!userResponse.ok) return null
    
    const userData = await userResponse.json()
    const etablissementId = userData?.etablissementId
    if (!etablissementId) return null
    
    // Step 2: Get establishment info using etablissementId
    const establishmentResponse = await fetch(`${API_URL}/public/etablissement/info/${etablissementId}`, {
      credentials: 'include'
    })
    if (!establishmentResponse.ok) return null
    
    const establishmentData = await establishmentResponse.json()
    return {
      name: establishmentData?.name || '',
      logo: establishmentData?.logo || ''
    }
  } catch (error) {
    // console.error('Error fetching establishment data:', error)
    return null
  }
}

// Helper function to load image as base64
const loadImageAsBase64 = async (url: string): Promise<string> => {
  try {
    const response = await fetch(url)
    const blob = await response.blob()
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  } catch (error) {
    // console.error('Error loading image:', error)
    return ''
  }
}

const drawClassPage = async (
  pdf: jsPDF,
  className: string,
  timetableData: any,
  timeSlotData: any[] = []
) => {
  // Initialize Arabic font support (like Quiz.ts)
  pdf.addFileToVFS(`${arabicFontName}.ttf`, arabicFontBase64)
  pdf.addFont(`${arabicFontName}.ttf`, arabicFontName, 'normal')
  
  // Set document properties (like Quiz.ts)
  pdf.setFont(arabicFontName)
  
  const pageWidth = 297 // A4 landscape width in mm
  const margin = 10
  const schoolName = timetableData?.schoolName || 'School Timetable'
  const address = timetableData?.address || 'School Address'
  
  // Get establishment data
  const establishment = await fetchEstablishmentData()
  const establishmentName = establishment?.name || schoolName
  const logoUrl = establishment?.logo
  let logoData = ''

  // Add compact header with logo and school name
  const headerHeight = 20
  pdf.setFillColor(255, 255, 255)
  pdf.rect(0, 0, pageWidth, headerHeight, 'F')
  
  // Add smaller logo in top-left corner
  const logoSize = 15
  const logoX = margin
  const logoY = 3
  
  // Try to load and add logo
  if (logoUrl) {
    try {
      logoData = await loadImageAsBase64(logoUrl)
      if (logoData) {
        pdf.addImage(logoData, 'PNG', logoX, logoY, logoSize, logoSize)
        // Add school name next to logo
        renderTextWithArabicSupport(pdf, establishmentName, logoX + logoSize + 3, logoY + 5, {
          fontSize: 9,
          fontStyle: 'bold',
          align: 'left'
        })
      }
    } catch (error) {
      // console.error('Error adding logo:', error)
    }
  }
  
  // Compact header content (centered and smaller)
  renderTextWithArabicSupport(pdf, schoolName, pageWidth / 2, 8, {
    fontSize: 12,
    fontStyle: 'bold',
    align: 'center'
  })
  renderTextWithArabicSupport(pdf, className, pageWidth / 2, 15, {
    fontSize: 10,
    fontStyle: 'bold',
    align: 'center'
  })
  renderTextWithArabicSupport(pdf, address, pageWidth / 2, 18, {
    fontSize: 7,
    fontStyle: 'normal',
    align: 'center'
  })

  const classData = getClassData(className, timetableData)

  // Always show all weekdays, regardless of whether they have data
  const allDays = getAllWeekdays()

  // Get time slots - if no class data exists, use default or timeSlotData
  let timeSlots: string[] = []
  if (classData) {
    timeSlots = getTimeSlots(classData, allDays, timeSlotData)
  } else if (timeSlotData && timeSlotData.length > 0) {
    timeSlots = [...timeSlotData]
      .sort((a, b) => a.period - b.period)
      .map(
        (slot) =>
          `${formatTimeForDisplay(slot.start)} => ${formatTimeForDisplay(slot.end)}`
      )
  } else {
    // Default time slots if no data available
    timeSlots = [
      '8 => 9',
      '9 => 10',
      '10 => 11',
      '11 => 12',
      '13 => 14',
      '14 => 15',
      '15 => 16',
      '16 => 17',
    ]
  }

  const pageHeight = pdf.internal.pageSize.height
  // Draw compact timetable table
  const tableTop = 25
  const tableWidth = pageWidth - margin * 2
  const colWidth = tableWidth / (allDays.length + 1)
  const headerRowHeight = 8  // Much smaller header
  const rowHeight = 14  // Smaller row height

  // Draw table header
  pdf.setFillColor(240, 240, 240)
  pdf.rect(margin, tableTop, tableWidth, headerRowHeight, 'F')
  
  renderTextWithArabicSupport(pdf, 'Time', margin + colWidth / 2, tableTop + headerRowHeight / 2, {
    fontSize: 8,
    fontStyle: 'bold',
    align: 'center',
    baseline: 'middle'
  })

  // Draw ALL day headers (Monday through Sunday)
  allDays.forEach((day, index) => {
    const x = margin + colWidth + index * colWidth
    renderTextWithArabicSupport(pdf, day, x + colWidth / 2, tableTop + headerRowHeight / 2, {
      fontSize: 8,
      fontStyle: 'bold',
      align: 'center',
      baseline: 'middle'
    })
  })

  // Draw time slots and lessons
  timeSlots.forEach((time, rowIndex) => {
    const y = tableTop + headerRowHeight + rowIndex * rowHeight

    // Check if this is a break time
    const isBreak = timeSlotData.some((slot) => {
      const formattedTime = `${formatTimeForDisplay(slot.start)} => ${formatTimeForDisplay(slot.end)}`
      return formattedTime === time && slot.isBreak
    })

    // Highlight break times
    if (isBreak) {
      pdf.setFillColor(255, 243, 205) // Light yellow background for breaks
      pdf.rect(margin, y, colWidth, rowHeight, 'F')
    }

    // Draw time slot with formatted time
    renderTextWithArabicSupport(pdf, time, margin + colWidth / 2, y + rowHeight / 2, {
      fontSize: 7,
      fontStyle: 'normal',
      align: 'center',
      baseline: 'middle'
    })

    // Draw lessons for ALL days (including empty ones)
    allDays.forEach((day, colIndex) => {
      const x = margin + colWidth + colIndex * colWidth

      // Look for lesson data - will be null/undefined for days with no data
      const lesson =
        classData && classData[day]
          ? classData[day].find((l: any) => l?.time === time)
          : null

      if (lesson) {
        pdf.setDrawColor(200, 200, 200)
        pdf.rect(x, y, colWidth, rowHeight, 'S')

        // Handle group lessons - UPDATED WITH ROOM NAMES
        if (lesson.group1 || lesson.group2) {
          // Check if this is alternating with another subject
          const alternateWith = lesson.alternateWith || lesson.group1?.alternateWith || lesson.group2?.alternateWith
          const isAlternatingWithSubject = alternateWith && alternateWith !== 'null' && alternateWith !== 'unknown'
          
          // Also check if we have group2 data or if alternateWith indicates we should show G2
          const hasGroup2Data = lesson.group2 && lesson.group2.subject && lesson.group2.subject !== 'null'
          const shouldShowG2 = isAlternatingWithSubject || hasGroup2Data
          
          // Show G1
          if (lesson.group1) {
            let subjectName = truncateText(lesson.group1.subject || '', 15)
            let teacherName = cleanTeacherName(lesson.group1.teacher || '')
            const roomName = (lesson.group1.salle || lesson.group1.room || '').trim()
            
            const g1Text = teacherName ? `G1: ${subjectName} (${teacherName})` : `G1: ${subjectName}`
            
            // Position G1 text higher to make room for the room name
            const g1TextY = shouldShowG2 ? y + 2 : y + 3
            const g1RoomY = shouldShowG2 ? y + 5 : y + 7
            
            renderTextWithArabicSupport(pdf, g1Text, x + colWidth / 2, g1TextY, {
              fontSize: 6,
              fontStyle: 'bold',
              align: 'center',
              maxWidth: colWidth - 2
            })
            
            // Show G1 room name
            if (roomName) {
              renderTextWithArabicSupport(pdf, roomName, x + colWidth / 2, g1RoomY, {
                fontSize: 5,
                fontStyle: 'normal',
                align: 'center',
                maxWidth: colWidth - 2
              })
            }
          }

          // Show separator and G2 if we should show G2
          if (shouldShowG2) {
            // Draw horizontal separator line
            pdf.setDrawColor(180, 180, 180)
            pdf.line(x, y + rowHeight / 2, x + colWidth, y + rowHeight / 2)

            // Get G2 data - either from group2 or from the alternateWith subject
            let group2Subject = ''
            let group2Teacher = ''
            let group2Room = ''
            
            if (lesson.group2 && lesson.group2.subject) {
              group2Subject = lesson.group2.subject
              group2Teacher = lesson.group2.teacher || ''
              group2Room = (lesson.group2.salle || lesson.group2.room || '').trim()
            } else if (isAlternatingWithSubject && alternateWith !== 'null') {
              // If we don't have group2 data but have alternateWith, try to get the subject name
              group2Subject = alternateWith // This might be the subject ID, we'll need to resolve it
              group2Teacher = '' // We don't have teacher info for the alternate subject
              group2Room = '' // We don't have room info for the alternate subject
            }
            
            if (group2Subject && group2Subject !== 'null') {
              group2Subject = truncateText(group2Subject, 15)
              group2Teacher = cleanTeacherName(group2Teacher)
              
              const g2Text = group2Teacher ? `G2: ${group2Subject} (${group2Teacher})` : `G2: ${group2Subject}`
              
              // Position G2 text and room
              const g2TextY = y + rowHeight / 2 + 2
              const g2RoomY = y + rowHeight / 2 + 5
              
              renderTextWithArabicSupport(pdf, g2Text, x + colWidth / 2, g2TextY, {
                fontSize: 6,
                fontStyle: 'bold',
                align: 'center',
                maxWidth: colWidth - 2
              })
              
              // Show G2 room name
              if (group2Room) {
                renderTextWithArabicSupport(pdf, group2Room, x + colWidth / 2, g2RoomY, {
                  fontSize: 5,
                  fontStyle: 'normal',
                  align: 'center',
                  maxWidth: colWidth - 2
                })
              }
            }
          }
        } else {
          // Handle regular lessons - FIXED LAYOUT
          let subjectName = truncateText(lesson.subject || '', 20)
          let teacherName = cleanTeacherName(lesson.teacher || '')
          const roomName = (lesson.salle || lesson.room || '').trim()
          
          // Calculate positions based on available content
          let subjectY = y + 3
          let teacherY = y + 6
          let roomY = y + rowHeight - 2 // Always position room at the bottom edge
          
          // If we have both subject and teacher, adjust positioning
          if (subjectName && teacherName) {
            subjectY = y + 2
            teacherY = y + 5
          } else if (subjectName && !teacherName) {
            // Only subject, center it more
            subjectY = y + 4
          }
          
          // Render subject name (truncated if too long)
          if (subjectName) {
            renderTextWithArabicSupport(pdf, subjectName, x + colWidth / 2, subjectY, {
              fontSize: 7,
              fontStyle: 'bold',
              align: 'center',
              maxWidth: colWidth - 2
            })
          }
          
          // Render teacher name if available
          if (teacherName) {
            renderTextWithArabicSupport(pdf, `(${teacherName})`, x + colWidth / 2, teacherY, {
              fontSize: 6,
              fontStyle: 'normal',
              align: 'center',
              maxWidth: colWidth - 2
            })
          }

          // Show room at the bottom edge - always at the same position
          if (roomName) {
            renderTextWithArabicSupport(pdf, roomName, x + colWidth / 2, roomY, {
              fontSize: 5,
              fontStyle: 'normal',
              align: 'center',
              maxWidth: colWidth - 2
            })
          }
        }
      } else {
        // Draw empty cell for days with no lessons
        pdf.setDrawColor(220, 220, 220)
        pdf.rect(x, y, colWidth, rowHeight, 'S')
      }
    })

    pdf.setDrawColor(200, 200, 200)
    pdf.rect(margin, y, colWidth, rowHeight, 'S')
  })

  // Draw outer table border
  pdf.setDrawColor(0, 0, 0)
  pdf.rect(
    margin,
    tableTop,
    tableWidth,
    headerRowHeight + rowHeight * timeSlots.length,
    'S'
  )

  // Add footer
  renderTextWithArabicSupport(
    pdf,
    `Generated on: ${new Date().toLocaleDateString()}`,
    pageWidth - margin,
    pageHeight - 5,
    {
      fontSize: 8,
      fontStyle: 'normal',
      align: 'right'
    }
  )
}

// Main export function
export const exportToPdf = async (
  getClasses: () => string[],
  getDisplayData: () => any,
  selectedClass: string,
  setSelectedClass: (className: string) => void,
  setIsPdfExporting: (isExporting: boolean) => void,
  timeSlotData: any[] = []
) => {
  try {
    setIsPdfExporting(true)
    toast({
      title: 'Exporting Timetable',
      description: 'Please wait while we generate your PDF...',
    })

    const classes = getClasses()
    if (classes.length === 0) {
      toast({
        title: 'Export Failed',
        description: 'No classes found to export',
        variant: 'destructive',
      })
      return
    }

    const displayData = getDisplayData()
    const currentSelectedClass = selectedClass
    const pdf = new jsPDF('l', 'mm', 'a4')

    // Process each class and draw its page
    for (let i = 0; i < classes.length; i++) {
      if (i > 0) {
        pdf.addPage()
        // Set Arabic font on new page (like Quiz.ts)
        pdf.setFont(arabicFontName)
      }
      await drawClassPage(pdf, classes[i], displayData, timeSlotData)
    }

    setSelectedClass(currentSelectedClass || classes[0])
    pdf.save(`school_timetable.pdf`)

    toast({
      title: 'Export Successful',
      description: `Timetable for all ${classes.length} classes has been exported as a PDF`,
    })
  } catch (error) {
    // console.error('Error exporting to PDF:', error)
    toast({
      title: 'Export Failed',
      description: 'An error occurred while exporting the timetable',
      variant: 'destructive',
    })
  } finally {
    setIsPdfExporting(false)
  }
}